import { ConvertToSubitem, Completed, LearnMore, Warning } from '@vibe/icons';
import { Box, Flex, Icon, Text, Tooltip } from '@vibe/core';
import { EditableBoardSourceName } from '../EditableBoardSourceName';
import { EditableBoard } from '../EditableBoard';
import { useWindowResize } from '../../hooks/use-window-resize';
import { useSingleBoard } from '../../shared/hooks/use-boards';
import { extractErrorMessage } from '../../lib/utils';

export function extractBoardNameAndErrorMessage(singleBoardResult: ReturnType<typeof useSingleBoard>, boardId?: number) {
  const { board, isLoading, error } = singleBoardResult;
  let boardErrorMessage;

  if (board === null && !isLoading) {
    boardErrorMessage = 'The chosen board can’t be found or you don’t have permissions to access it.';
  } else if (error) {
    boardErrorMessage = extractErrorMessage(error) || 'An error occurred while loading the board.';
  }

  let boardName = '';

  if (isLoading) {
    boardName = 'Loading board...';
  } else if (board) {
    boardName = board.name;
  } else if (boardId) {
    boardName = `ID: ${boardId}`;
  }

  return { boardName, boardErrorMessage };
}

export function extractConnectBoardColumnErrorMessage(singleBoardResult: ReturnType<typeof useSingleBoard>, columnId?: string) {
  let connectBoardColumnErrorMessage;
  const { board } = singleBoardResult;

  if (columnId) {
    if (board && !singleBoardResult.isLoading) {
      const clientColumn = board.columns.find((col) => col.id === columnId);

      if (!clientColumn) {
        connectBoardColumnErrorMessage = 'Could not find the selected Connect Boards column on the board.';
      } else if (clientColumn.type !== 'board_relation') {
        connectBoardColumnErrorMessage = 'The selected column is not a Connect Boards column.';
      }
    } else if (board === null) {
      connectBoardColumnErrorMessage = 'Could not load the board to validate the selected Connect Boards column.';
    }
  }

  return connectBoardColumnErrorMessage;
}

type EditBoardFieldProps = {
  boardId?: number;
  headline: string;
  introText: string;
  helpText: string;
  value: string;
  errorMessage?: string;
  isCompleted: boolean;
  onChange: (boardId: number) => void;
};

export function EditBoardField({
  onChange,
  headline,
  introText,
  helpText,
  errorMessage,
  boardId,
  isCompleted,
  value,
}: EditBoardFieldProps) {
  return (
    <Box padding="large" backgroundColor={isCompleted ? 'primaryBackgroundColor' : 'greyBackgroundColor'}>
      <Flex
        direction="row"
        justify="start"
        align="center"
        gap={20}
        style={{
          width: '100%',
        }}
      >
        {errorMessage ? (
          <div style={{ transform: 'translate3d(0,2px,0)' }}>
            <Icon icon={Warning} size={24} />
          </div>
        ) : isCompleted ? (
          <div style={{ transform: 'translate3d(0,2px,0)' }}>
            <Icon icon={Completed} size={24} />
          </div>
        ) : (
          <div style={{ transform: 'scaleX(-1) rotate(90deg) translate3d(-3px,2px,0)' }}>
            <Icon icon={ConvertToSubitem} size={24} />
          </div>
        )}
        <Flex
          direction="column"
          justify="start"
          align="start"
          gap={2}
          style={{
            width: 'inherit',
            maxWidth: 'calc(100% - 230px)',
          }}
        >
          <Flex gap={8} style={{ maxWidth: '100%' }}>
            <Text
              type="text1"
              align="start"
              color="primary"
              weight="bold"
              style={{
                maxWidth: '100%',
              }}
            >
              {isCompleted ? headline : introText}
            </Text>
            {isCompleted ? (
              <Tooltip content={helpText} position="right" showOnDialogEnter={false}>
                <Icon icon={LearnMore} size={16} style={{ transform: 'translate3d(0,2px,0)' }} />
              </Tooltip>
            ) : null}
          </Flex>
          <Text
            type="text2"
            align="start"
            color="secondary"
            weight="normal"
            style={{
              maxWidth: '100%',
            }}
          >
            {isCompleted ? value : helpText}
          </Text>
        </Flex>

        <EditableBoard
          buttonMode="choose"
          selectedBoardId={boardId}
          onChange={(newSelectedBoardId) => {
            if (newSelectedBoardId) {
              onChange(newSelectedBoardId);
            }
          }}
        />
      </Flex>
      {errorMessage ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '16px',
            marginTop: '16px',
            padding: '10px 16px',
            border: '1px solid #ecb7bf',
            borderRadius: '4px',
            backgroundColor: '#f4c3cb',
          }}
        >
          <Text type="text3" weight="normal" align="center" color="primary" style={{ flex: 1 }}>
            {errorMessage}
          </Text>
        </div>
      ) : null}
    </Box>
  );
}

type EditBoardSourceNameFieldProps = {
  isCompleted: boolean;
  headline: string;
  introText: string;
  helpText: string;
  value: string;
  boardSourceName: string;
  onChange: (boardSourceName: string) => void;
};

export function EditBoardSourceNameField({
  onChange,
  headline,
  helpText,
  introText,
  value,
  boardSourceName,
  isCompleted,
}: EditBoardSourceNameFieldProps) {
  return (
    <Box padding="large" backgroundColor={isCompleted ? 'primaryBackgroundColor' : 'greyBackgroundColor'}>
      <Flex
        direction="row"
        justify="start"
        align="center"
        gap={20}
        style={{
          width: '100%',
        }}
      >
        {isCompleted ? (
          <div style={{ transform: 'translate3d(0,2px,0)' }}>
            <Icon icon={Completed} size={24} />
          </div>
        ) : (
          <div style={{ transform: 'scaleX(-1) rotate(90deg) translate3d(-3px,2px,0)' }}>
            <Icon icon={ConvertToSubitem} size={24} />
          </div>
        )}
        <Flex
          direction="column"
          justify="start"
          align="start"
          gap={2}
          style={{
            width: 'inherit',
            maxWidth: 'calc(100% - 330px)' /* 230px */,
          }}
        >
          <Flex gap={8} style={{ maxWidth: '100%' }}>
            <Text
              type="text1"
              align="start"
              color="primary"
              weight="bold"
              style={{
                maxWidth: '100%',
              }}
            >
              {isCompleted ? headline : introText}
            </Text>
            {isCompleted ? (
              <Tooltip content={helpText} position="right" showOnDialogEnter={false}>
                <Icon icon={LearnMore} size={16} style={{ transform: 'translate3d(0,2px,0)' }} />
              </Tooltip>
            ) : null}
          </Flex>
          <Text
            type="text2"
            align="start"
            color="secondary"
            weight="normal"
            style={{
              maxWidth: '100%',
            }}
          >
            {isCompleted ? value : helpText}
          </Text>
        </Flex>

        <EditableBoardSourceName
          boardSourceName={boardSourceName}
          onChange={(newBoardSourceName: string) => {
            onChange(newBoardSourceName);
          }}
        />
      </Flex>
    </Box>
  );
}

type BoardSourceIdProps = {
  sourceId: string;
  headline: string;
  helpText: string;
};

export function BoardSourceId({ sourceId, headline, helpText }: BoardSourceIdProps) {
  return (
    <Box padding="large" backgroundColor="primaryBackgroundColor">
      <Flex
        direction="row"
        justify="start"
        align="center"
        gap={20}
        style={{
          width: '100%',
        }}
      >
        <div style={{ transform: 'translate3d(0,2px,0)' }}>
          <Icon icon={Completed} size={24} />
        </div>
        <Flex
          direction="column"
          justify="start"
          align="start"
          gap={2}
          style={{
            width: 'inherit',
            maxWidth: 'calc(100% - 230px)',
          }}
        >
          <Flex gap={8} style={{ maxWidth: '100%' }}>
            <Text
              type="text1"
              align="start"
              color="primary"
              weight="bold"
              style={{
                maxWidth: '100%',
              }}
            >
              {headline}
            </Text>
            <Tooltip content={helpText} position="right" showOnDialogEnter={false}>
              <Icon icon={LearnMore} size={16} style={{ transform: 'translate3d(0,2px,0)' }} />
            </Tooltip>
          </Flex>
          <Text
            type="text2"
            align="start"
            color="secondary"
            weight="normal"
            style={{
              maxWidth: '100%',
            }}
          >
            {sourceId}
          </Text>
        </Flex>
      </Flex>
    </Box>
  );
}

type BoardSourceExplainerProps = {
  title: string;
  description: string;
  videoSrc: string;
};

export function BoardSourceExplainer({ title, description, videoSrc }: BoardSourceExplainerProps) {
  const { width: windowWidth } = useWindowResize();

  let screenMode: 'small' | 'medium' | 'large' = 'large';

  if (windowWidth < 1250) {
    screenMode = 'medium';
  }

  if (windowWidth < 1000) {
    screenMode = 'small';
  }

  return (
    <div style={{ marginBottom: '24px' }}>
      <Box padding="xl" rounded="small" border borderColor="layoutBorderColor" backgroundColor="greyBackgroundColor">
        <Flex
          justify="start"
          align="start"
          gap={screenMode === 'large' ? 40 : 24}
          style={{
            flexDirection: screenMode === 'large' ? 'row' : 'column',
            width: '100%',
          }}
        >
          <Flex
            direction="column"
            justify="start"
            align="start"
            gap={8}
            style={{
              width: screenMode === 'large' ? '30%' : '100%',
              minWidth: '150px',
              color: '#0073ea',
            }}
          >
            <Text type="text1" align="start" color="inherit" weight="medium" element="div" ellipsis={false} style={{ maxWidth: '100%' }}>
              {title}
            </Text>
            <Text type="text2" align="start" color="secondary" element="div" ellipsis={false} style={{ maxWidth: '100%' }}>
              {description}
            </Text>
          </Flex>
          <Box
            rounded="medium"
            backgroundColor="primaryBackgroundColor"
            style={{
              aspectRatio: '2362 / 1080',
              flex: 1,
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
              lineHeight: 0,
            }}
          >
            <video src={videoSrc} muted loop autoPlay controls style={{ width: '100%', height: 'auto' }} />
          </Box>
        </Flex>
      </Box>
    </div>
  );
}
